import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart, Check, X, Clock, Filter, User, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const STATUS_STYLES = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approuvee', cls: 'bg-emerald-100 text-emerald-700', icon: Check },
  rejected: { label: 'Refusee', cls: 'bg-red-100 text-red-700', icon: X },
};

const URGENCY_STYLES = {
  normal: { label: 'Normal', cls: 'text-slate-500' },
  urgent: { label: 'Urgent', cls: 'text-red-600 font-bold' },
};

const CommandesAdminPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [commandes, setCommandes] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const role = auth?.admin?.role || 'admin';
  const adminName = `${auth?.admin?.first_name || ''} ${auth?.admin?.last_name || ''}`.trim();

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (tab !== 'all' && tab !== 'stock_alerts') params.source = tab;
      if (filterStatus !== 'all') params.status = filterStatus;
      const [cmdRes, alertRes] = await Promise.all([
        axios.get(`${API}/commandes`, { params }),
        axios.get(`${API}/atelier/stock-alerts`).catch(() => ({ data: [] })),
      ]);
      setCommandes(cmdRes.data);
      setStockAlerts(alertRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [tab, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const approve = async (id) => {
    try {
      await axios.put(`${API}/commandes/${id}/approve`, { reviewed_by: adminName });
      toast.success('Commande approuvee');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const reject = async (id) => {
    try {
      await axios.put(`${API}/commandes/${id}/reject`, { reviewed_by: adminName });
      toast.success('Commande refusee');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const pendingCount = commandes.filter(c => c.status === 'pending').length;
  const openAlertsCount = stockAlerts.filter(a => a.status === 'open').length;
  const tabs = [
    { key: 'all', label: 'Toutes' },
    { key: 'atelier', label: 'Atelier' },
    { key: 'reception', label: 'Reception' },
    { key: 'stock_alerts', label: `Alertes stock${openAlertsCount > 0 ? ` (${openAlertsCount})` : ''}` },
  ];

  const resolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/atelier/stock-alerts/${alertId}/resolve`, { resolved_by: adminName });
      toast.success('Alerte resolue');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="commandes-admin-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-blue-600" /> Commandes & Besoins
            </h1>
            {pendingCount > 0 && (
              <p className="text-sm text-amber-600 font-medium mt-1">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                {pendingCount} demande{pendingCount > 1 ? 's' : ''} en attente
              </p>
            )}
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="filter-status">
              <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="pending">{t('common.pending')}</SelectItem>
              <SelectItem value="approved">Approuvees</SelectItem>
              <SelectItem value="rejected">Refusees</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Source Tabs */}
        <div className="flex gap-2">
          {tabs.map(t => (
            <Button
              key={t.key}
              variant={tab === t.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <p className="text-center text-slate-400 py-10">{t('common.loading')}</p>
        ) : tab === 'stock_alerts' ? (
          stockAlerts.length === 0 ? (
            <Card className="p-10 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune alerte stock</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {stockAlerts.map(alert => (
                <Card key={alert.id} className={`p-4 ${alert.status === 'open' ? 'border-l-4 border-l-red-400' : ''}`} data-testid={`alert-${alert.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{alert.item_name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${alert.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {alert.status === 'open' ? 'En rupture' : 'Resolu'}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{alert.category}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{alert.reported_by}</span>
                        <span>{new Date(alert.reported_at).toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {alert.description && <p className="text-xs text-slate-500 mt-1 italic">"{alert.description}"</p>}
                      {alert.resolved_by && <p className="text-xs text-slate-400 mt-1">Resolu par {alert.resolved_by}</p>}
                    </div>
                    {alert.status === 'open' && role === 'admin' && (
                      <Button size="sm" onClick={() => resolveAlert(alert.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`resolve-${alert.id}`}>
                        <Check className="w-4 h-4 mr-1" /> Resolu
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )
        ) : commandes.length === 0 ? (
          <Card className="p-10 text-center">
            <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune demande</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {commandes.map(cmd => {
              const st = STATUS_STYLES[cmd.status] || STATUS_STYLES.pending;
              const urg = URGENCY_STYLES[cmd.urgency] || URGENCY_STYLES.normal;
              const StIcon = st.icon;
              return (
                <Card key={cmd.id} className={`p-4 ${cmd.status === 'pending' ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`cmd-${cmd.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{cmd.item_name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>
                          <StIcon className="w-3 h-3 inline mr-0.5" />{st.label}
                        </span>
                        {cmd.urgency === 'urgent' && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full bg-red-100 ${urg.cls}`}>URGENT</span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${cmd.source === 'atelier' ? 'bg-blue-50 text-blue-600' : 'bg-violet-50 text-violet-600'}`}>
                          {cmd.source === 'atelier' ? 'Atelier' : 'Reception'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                        <span>{cmd.category}</span>
                        <span>Qte: {cmd.quantity} {cmd.unit || ''}</span>
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{cmd.requested_by}</span>
                        <span>{new Date(cmd.requested_at).toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {cmd.description && <p className="text-xs text-slate-500 mt-1 italic">"{cmd.description}"</p>}
                      {cmd.reviewed_by && (
                        <p className="text-xs text-slate-400 mt-1">
                          {cmd.status === 'approved' ? 'Approuvee' : 'Refusee'} par {cmd.reviewed_by}
                          {cmd.reviewed_at && ` le ${new Date(cmd.reviewed_at).toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
                        </p>
                      )}
                    </div>

                    {/* Admin-only approve/reject */}
                    {cmd.status === 'pending' && role === 'admin' && (
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" onClick={() => approve(cmd.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${cmd.id}`}>
                          <Check className="w-4 h-4 mr-1" /> Accepter
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(cmd.id)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-${cmd.id}`}>
                          <X className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CommandesAdminPage;
